/**
 * 统一时间格式化工具
 *
 * 兼容新旧数据格式：
 * - 旧格式: "2026-08-03 13:00:00" (视为 Asia/Shanghai/UTC+8)
 * - 新格式: "2026-08-03T05:00:00.000Z" (UTC ISO8601)
 * - 带时区: "2026-08-03T13:00:00+08:00" (本地时间)
 *
 * 用法：import { fmtTime, fmtDateTime, fmtDate, fmtShort, formatTime } from '@/utils/time'
 */

/**
 * 自动识别时间字符串并转换为本地时间对象
 * @param {string} timeStr 时间字符串
 * @returns {Date|null} 本地时间 Date 对象
 */
function parseTime(timeStr) {
  if (!timeStr) return null;

  // 旧格式: "YYYY-MM-DD HH:mm:ss" → 视为 Asia/Shanghai (UTC+8)
  if (timeStr.includes(' ') && !timeStr.includes('T')) {
    // 补全为 ISO 格式并添加时区
    const isoStr = timeStr.replace(' ', 'T') + '+08:00';
    const date = new Date(isoStr);
    return isNaN(date.getTime()) ? null : date;
  }

  // ISO 格式
  if (timeStr.includes('T')) {
    const date = new Date(timeStr);
    return isNaN(date.getTime()) ? null : date;
  }

  // 尝试直接解析
  const date = new Date(timeStr);
  return isNaN(date.getTime()) ? null : date;
}

/**
 * 格式化时间字符串 (兼容新旧格式，直接字符串处理)
 * @param {string} timeStr 时间字符串
 * @returns {string} 格式: "2026-08-03 13:00:00"
 */
function formatTime(timeStr) {
  if (!timeStr) return '';

  // 新格式 (ISO): 先转为本地时间
  if (timeStr.includes('T')) {
    const date = parseTime(timeStr);
    if (!date) return '';

    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
  }

  // 旧格式: 直接返回截断
  const str = String(timeStr).replace('T', ' ');
  return str.length >= 19 ? str.substring(0, 19) : (str.length >= 16 ? str.substring(0, 16) : str);
}

/**
 * 完整日期时间：2026-07-31 14:30:25 (向后兼容)
 * @param {string} s 时间字符串
 * @returns {string} 格式: "2026-07-31 14:30:25"
 */
export function fmtDateTime(s) {
  if (!s) return '-';
  return formatTime(s) || '-';
}

/**
 * 日期时间（不含秒）：2026-07-31 14:30 (向后兼容)
 * @param {string} s 时间字符串
 * @returns {string} 格式: "2026-07-31 14:30"
 */
export function fmtTime(s) {
  if (!s) return '-';
  const formatted = formatTime(s);
  return formatted && formatted !== '-' ? formatted.substring(0, 16) : '-';
}

/**
 * 仅日期：2026-07-31 (向后兼容)
 * @param {string} s 时间字符串
 * @returns {string} 格式: "2026-07-31"
 */
export function fmtDate(s) {
  if (!s) return '-';
  const formatted = formatTime(s);
  return formatted && formatted !== '-' ? formatted.substring(0, 10) : '-';
}

/**
 * 紧凑（月日时分）：07-31 14:30 (向后兼容)
 * @param {string} s 时间字符串
 * @returns {string} 格式: "07-31 14:30"
 */
export function fmtShort(s) {
  if (!s) return '-';
  const formatted = formatTime(s);
  if (formatted && formatted !== '-' && formatted.length >= 16) {
    return formatted.substring(5, 16);
  }
  return '-';
}

/**
 * 格式化为本地时间字符串 (短格式)
 * @param {string} timeStr 时间字符串
 * @returns {string} 格式: "2026-08-03 13:00"
 */
export function formatTimeShort(timeStr) {
  const formatted = formatTime(timeStr);
  return formatted ? formatted.substring(0, 16) : '';
}

/**
 * 格式化为中文本地时间 (完整)
 * @param {string} timeStr 时间字符串
 * @returns {string} 格式: "2026/8/3 13:00:00"
 */
export function formatTimeChinese(timeStr) {
  const date = parseTime(timeStr);
  if (!date) return '';

  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });
}

/**
 * 格式化为中文本地时间 (不含秒)
 * @param {string} timeStr 时间字符串
 * @returns {string} 格式: "2026/8/3 13:00"
 */
export function formatTimeChineseShort(timeStr) {
  const date = parseTime(timeStr);
  if (!date) return '';

  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
}

/**
 * 格式化为中文日期
 * @param {string} timeStr 时间字符串
 * @returns {string} 格式: "2026年8月3日"
 */
export function formatDateChinese(timeStr) {
  const date = parseTime(timeStr);
  if (!date) return '';

  return date.toLocaleDateString('zh-CN', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

/**
 * 格式化为时间字符串 (不含日期)
 * @param {string} timeStr 时间字符串
 * @returns {string} 格式: "13:00:00"
 */
export function formatTimeOnly(timeStr) {
  const date = parseTime(timeStr);
  if (!date) return '';

  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/**
 * 获取相对时间描述
 * @param {string} timeStr 时间字符串
 * @returns {string} 例如: "刚刚", "5分钟前", "2小时前", "3天前"
 */
export function formatRelativeTime(timeStr) {
  const date = parseTime(timeStr);
  if (!date) return '';

  const now = new Date();
  const diff = now - date;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return '刚刚';
  if (minutes < 60) return `${minutes}分钟前`;
  if (hours < 24) return `${hours}小时前`;
  if (days < 30) return `${days}天前`;
  return fmtDate(timeStr);
}

/**
 * 获取日期标签 (今天/昨天/日期)
 * @param {string} timeStr 时间字符串
 * @returns {string} 例如: "今天", "昨天", "2026-08-01"
 */
export function getDateLabel(timeStr) {
  const date = parseTime(timeStr);
  if (!date) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);

  const targetDate = new Date(date);
  targetDate.setHours(0, 0, 0, 0);

  if (targetDate.getTime() === today.getTime()) {
    return '今天';
  }
  if (targetDate.getTime() === yesterday.getTime()) {
    return '昨天';
  }

  return fmtDate(timeStr);
}

/**
 * 人性化时间显示：今天 HH:mm / 昨天 HH:mm / YYYY-MM-DD HH:mm
 * @param {string} timeStr 时间字符串
 * @returns {string} 例如: "今天 15:30", "昨天 09:12", "2026-08-01 14:00"
 */
export function formatTimeFriendly(timeStr) {
  const date = parseTime(timeStr);
  if (!date) return '';

  const pad = (n) => String(n).padStart(2, '0');
  const time = `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  const label = getDateLabel(timeStr);

  if (label === '今天') return `今天 ${time}`;
  if (label === '昨天') return `昨天 ${time}`;
  return `${label} ${time}`;
}

/**
 * 格式化为时间段 (开始时间 ~ 结束时间)
 * @param {string} startTimeStr 开始时间
 * @param {string} endTimeStr 结束时间
 * @returns {string} 例如: "2026-08-01 ~ 2026-08-10"
 */
export function formatTimeRange(startTimeStr, endTimeStr) {
  const start = fmtDate(startTimeStr);
  const end = fmtDate(endTimeStr);
  return start && end && start !== '-' && end !== '-' ? `${start} ~ ${end}` : '';
}

export default {
  parseTime,
  formatTimeShort,
  formatTime,
  formatTimeChinese,
  formatTimeChineseShort,
  formatTimeOnly,
  formatRelativeTime,
  getDateLabel,
  formatTimeFriendly,
  formatTimeRange,
  // 向后兼容
  fmtDateTime,
  fmtTime,
  fmtDate,
  fmtShort
};
